/**
 * BootstrapWaitBanner — top-of-page strip while
 * generateTypedReport's wait-for-bootstrap loop is running.
 *
 * Without this banner the user fires a report from the wizard, the
 * wizard closes, and they see only the global progress bar — no
 * indication that the *evidence* is the bottleneck. The banner reads
 * the same store fields the global progress bar uses (`isLoading` +
 * `progressStage`), but matches on the specific bootstrap-wait copy
 * exported by sedBootstrap so a generic "Generating report…" stage
 * doesn't trigger it.
 *
 * Progressive copy at 30s and 50s gives the user signal that the
 * wait is bounded — every state increment is a reassurance, not a
 * surprise. The banner disappears the moment the wait copy clears
 * (bootstrap settled, report fell through to load, or the action
 * errored out).
 */
import React from 'react';
import { Loader2 } from 'lucide-react';
import { useStore } from '../../store';
import {
  MAX_BOOTSTRAP_WAIT_MS,
  BOOTSTRAP_WAIT_PROGRESS_MESSAGE,
} from '../../services/sedBootstrap';

const ELAPSED_30S_THRESHOLD_MS = 30_000;
const ELAPSED_50S_THRESHOLD_MS = 50_000;

function copyForElapsed(elapsedMs: number): string {
  if (elapsedMs >= ELAPSED_50S_THRESHOLD_MS) {
    return 'Almost timed out — falling back to current evidence.';
  }
  if (elapsedMs >= ELAPSED_30S_THRESHOLD_MS) {
    return 'Still waiting on evidence — almost there.';
  }
  const seconds = Math.round(MAX_BOOTSTRAP_WAIT_MS / 1000);
  return `Waiting for evidence collection to finish before generating your report (up to ${seconds}s)…`;
}

export const BootstrapWaitBanner: React.FC = () => {
  // Narrow per-field selectors so the banner re-renders only on the
  // two store slices it actually uses (not on every siteRag mutation).
  const isLoading = useStore(s => s.siteRag.isLoading);
  const progressStage = useStore(s => s.siteRag.progressStage);

  const isWaitingForBootstrap =
    isLoading && (progressStage ?? '').startsWith(BOOTSTRAP_WAIT_PROGRESS_MESSAGE);

  // Track the moment the wait started so we can drive the progressive
  // copy. Reset whenever the wait flag flips back on (e.g. user fires
  // a second report in the same session).
  const [startedAtMs, setStartedAtMs] = React.useState<number | null>(null);
  const [elapsedMs, setElapsedMs] = React.useState(0);

  React.useEffect(() => {
    if (!isWaitingForBootstrap) {
      setStartedAtMs(null);
      setElapsedMs(0);
      return;
    }
    const start = Date.now();
    setStartedAtMs(start);
    setElapsedMs(0);
    const interval = setInterval(() => {
      setElapsedMs(Date.now() - start);
    }, 1000);
    return () => clearInterval(interval);
  }, [isWaitingForBootstrap]);

  if (!isWaitingForBootstrap) return null;

  const copy = copyForElapsed(startedAtMs ? elapsedMs : 0);

  return (
    <div
      data-testid="bootstrap-wait-banner"
      data-elapsed-ms={elapsedMs}
      role="status"
      aria-live="polite"
      className="w-full px-6 py-2 border-b border-warning/40 bg-warning/10 flex items-center gap-3 type-meta text-text-primary"
    >
      <Loader2 className="w-3.5 h-3.5 animate-spin text-warning shrink-0" />
      <span className="flex-1">{copy}</span>
    </div>
  );
};
